import { Link } from "react-router-dom";
import { Calculator, ArrowRight, Sparkles, Images } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import video1 from "@/assets/video-1.mp4";
import video2 from "@/assets/video-2.mp4";
import video3 from "@/assets/video-3.mp4";
import video4 from "@/assets/video-4.mp4";
import video5 from "@/assets/video-5.mp4";
import video6 from "@/assets/video-6.mp4";

const fallbackVideos = [video1, video2, video3, video4, video5, video6];

const CalculatorBanner = () => {
  const { t } = useTranslation();
  const [activeLayer, setActiveLayer] = useState<'a' | 'b'>('a');
  const [srcA, setSrcA] = useState(fallbackVideos[0]);
  const [srcB, setSrcB] = useState('');
  const [fadeA, setFadeA] = useState(true);
  const stats = t("calculatorBanner.stats", { returnObjects: true }) as { val: string; label: string }[];
  const indexRef = useRef(0);
  const videosRef = useRef(fallbackVideos);
  const videoARef = useRef<HTMLVideoElement>(null);
  const videoBRef = useRef<HTMLVideoElement>(null);
  const busyRef = useRef(false);

  // Fetch all videos from gallery_media
  useEffect(() => {
    let mounted = true;

    const fetchVideos = async () => {
      const { data } = await supabase
        .from("gallery_media")
        .select("url")
        .eq("type", "video")
        .order("position", { ascending: true });

      if (!mounted) return;

      const dbVideos = (data ?? []).map((v) => v.url).filter(Boolean);
      const selected = dbVideos.length >= 5
        ? dbVideos.slice(0, 5)
        : Array.from(new Set([...dbVideos, ...fallbackVideos])).slice(0, 5);
      videosRef.current = selected.length > 0 ? selected : fallbackVideos.slice(0, 5);
      indexRef.current = 0;
      setSrcA(videosRef.current[0]);
      setFadeA(true);
      setActiveLayer('a');
    };

    fetchVideos();

    const channel = supabase
      .channel("calculator-banner-videos")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "gallery_media" },
        (payload) => {
          const nextType = (payload.new as { type?: string } | null)?.type;
          const prevType = (payload.old as { type?: string } | null)?.type;
          if (nextType === "video" || prevType === "video") {
            fetchVideos();
          }
        },
      )
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, []);

  const waitForPlaying = (video: HTMLVideoElement): Promise<boolean> => {
    return new Promise((resolve) => {
      let settled = false;

      const finish = (result: boolean) => {
        if (settled) return;
        settled = true;
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('error', onError);
        resolve(result);
      };

      const onPlaying = () => {
        // Wait 2 frames so first decoded frame is painted
        requestAnimationFrame(() => {
          requestAnimationFrame(() => finish(true));
        });
      };

      const onError = () => finish(false);

      video.addEventListener('playing', onPlaying);
      video.addEventListener('error', onError);

      // If it never starts, cancel this switch and keep current video visible
      setTimeout(() => finish(false), 4500);
    });
  };

  const advance = useCallback(async () => {
    const len = videosRef.current.length;
    if (len <= 1 || busyRef.current) return;
    busyRef.current = true;

    const nextIdx = (indexRef.current + 1) % len;
    const nextSrc = videosRef.current[nextIdx];

    if (activeLayer === 'a') {
      setSrcB(nextSrc);
      await new Promise((r) => setTimeout(r, 50));

      let ready = false;
      if (videoBRef.current) {
        videoBRef.current.load();
        const playPromise = videoBRef.current.play();
        ready = await waitForPlaying(videoBRef.current);
        if (playPromise) await playPromise.catch(() => {});
      }

      if (!ready) {
        indexRef.current = nextIdx;
        busyRef.current = false;
        return;
      }

      setFadeA(false);
      setTimeout(() => {
        setActiveLayer('b');
        indexRef.current = nextIdx;
        busyRef.current = false;
      }, 800);
    } else {
      setSrcA(nextSrc);
      await new Promise((r) => setTimeout(r, 50));

      let ready = false;
      if (videoARef.current) {
        videoARef.current.load();
        const playPromise = videoARef.current.play();
        ready = await waitForPlaying(videoARef.current);
        if (playPromise) await playPromise.catch(() => {});
      }

      if (!ready) {
        indexRef.current = nextIdx;
        busyRef.current = false;
        return;
      }

      setFadeA(true);
      setTimeout(() => {
        setActiveLayer('a');
        indexRef.current = nextIdx;
        busyRef.current = false;
      }, 800);
    }
  }, [activeLayer]);

  useEffect(() => {
    const interval = setInterval(advance, 4000);
    return () => clearInterval(interval);
  }, [advance]);

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-navy-dark py-8 md:py-14 lg:py-16 pb-4 md:pb-8 lg:pb-10">
      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-32 h-32 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent/5 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
          {/* Text content */}
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-accent/15 border border-accent/30 rounded-full px-4 md:px-5 py-1.5 md:py-2 mb-4 md:mb-6">
              <Sparkles className="w-3.5 h-3.5 md:w-4 md:h-4 text-accent" />
              <span className="text-accent text-xs md:text-sm font-bold tracking-wide">{t("calculatorBanner.badge")}</span>
            </div>

            <h2 className="text-primary-foreground font-black text-2xl md:text-4xl lg:text-5xl uppercase leading-tight mb-3 md:mb-4">
              {t("calculatorBanner.title1")}{" "}
              <span className="text-accent">{t("calculatorBanner.titleAccent")}</span>
              <br className="hidden sm:block" />
              {t("calculatorBanner.title2")}
            </h2>

            <p className="text-primary-foreground/60 text-sm md:text-base lg:text-lg max-w-2xl mx-auto lg:mx-0 mb-6 md:mb-8">
              {t("calculatorBanner.description")}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 md:gap-4">
              <Link
                to="/kalkulator"
                className="gradient-gold text-accent-foreground font-black text-base md:text-lg uppercase tracking-wider px-12 md:px-16 lg:px-20 py-3.5 md:py-4 rounded-2xl shadow-gold hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-3 min-w-[280px] md:min-w-[340px]"
              >
                <Calculator className="w-5 h-5" />
                {t("calculatorBanner.cta")}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3 text-primary-foreground/40 text-xs md:text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                {t("calculatorBanner.trust")}
              </div>
            </div>

            {/* Mini preview stats */}
            <div className="grid grid-cols-3 gap-3 md:gap-4 max-w-md mx-auto lg:mx-0 mt-8 md:mt-10">
              {stats.map((s) => (
                <div key={s.label} className="text-center lg:text-left">
                  <p className="text-accent font-black text-xl md:text-2xl">{s.val}</p>
                  <p className="text-primary-foreground/40 text-[10px] md:text-xs uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Single video window */}
          <div className="w-56 md:w-64 lg:w-72 aspect-[9/16] rounded-2xl p-[3px] bg-gradient-to-br from-gold-light via-gold to-gold-dark shadow-gold">
            <div className="w-full h-full rounded-[13px] overflow-hidden bg-navy-dark relative">
              {/* Layer A */}
              <video
                ref={videoARef}
                src={srcA}
                muted
                playsInline
                loop
                autoPlay
                preload="auto"
                className={`absolute inset-0 w-full h-full object-cover -scale-x-100 transition-opacity duration-[800ms] ease-in-out ${fadeA ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
              />
              {/* Layer B */}
              <video
                ref={videoBRef}
                src={srcB}
                muted
                playsInline
                loop
                autoPlay
                preload="auto"
                className={`absolute inset-0 w-full h-full object-cover -scale-x-100 transition-opacity duration-[800ms] ease-in-out ${fadeA ? 'opacity-0 z-0' : 'opacity-100 z-10'}`}
              />
            </div>
          </div>
        </div>

        <div className="text-center mt-6 md:mt-8">
          <a
            href="#galeria"
            className="group inline-flex items-center gap-3 rounded-full border border-accent/40 bg-primary-foreground/5 backdrop-blur-sm px-6 md:px-8 py-3 md:py-3.5 text-primary-foreground font-bold text-sm md:text-base tracking-wide shadow-lg hover:border-accent hover:bg-accent/10 hover:shadow-gold transition-all duration-300"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent/20 text-accent group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
              <Images className="w-4 h-4" />
            </span>
            {t("calculatorBanner.galleryCta")}
            <ArrowRight className="w-4 h-4 text-accent transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>

      </div>
    </section>
  );
};

export default CalculatorBanner;
